/**
 * จัดข้อความบนปุ่ม rich menu ให้อยู่ในช่อง
 *
 * อยู่ใน shared เพราะทั้งหน้าเว็บ (วาดจริง) และเทสต์ฝั่ง Worker ต้องใช้ตรรกะเดียวกัน
 * ประกาศ TextMeasurer เองแทนการอ้าง CanvasRenderingContext2D เพื่อไม่ให้ shared
 * ต้องพึ่ง DOM — รันไทม์ Workers ไม่มี type พวกนั้น
 */

export interface TextMeasurer {
  font: string;
  measureText(text: string): { width: number };
}

const LABEL_FONT_MAX = 78;
/** เล็กกว่านี้อ่านไม่ออกบนมือถือ ถ้าถึงพื้นนี้แล้วยังไม่พอ ให้ตัดบรรทัดแทน */
const LABEL_FONT_MIN = 52;

export function setLabelFont(ctx: TextMeasurer, size: number): void {
  ctx.font = `bold ${size}px "IBM Plex Sans Thai", "Sarabun", system-ui, sans-serif`;
}

/**
 * หาขนาดตัวอักษรและการตัดบรรทัดที่ทำให้ป้ายอยู่ในช่องพอดี
 *
 * ภาษาไทยไม่มีช่องว่างระหว่างคำ การตัดบรรทัดด้วย split(' ') จึงใช้ไม่ได้เลย —
 * ป้ายอย่าง "ค้นหาผู้รับความช่วยเหลือ" เป็นก้อนเดียวที่ล้นทะลุเส้นแบ่งช่องออกไป
 * ทับปุ่มข้าง ๆ ผู้ใช้เห็นแล้วไม่รู้ว่าข้อความไหนเป็นของปุ่มไหน
 *
 * ย่อขนาดตัวอักษรก่อนเป็นอันดับแรก เพราะการตัดคำไทยกลางคำให้ผลที่อ่านแล้วสะดุด
 * (ต้องใช้พจนานุกรมถึงจะตัดถูกตำแหน่ง ซึ่งเกินความจำเป็นของป้ายสั้น ๆ)
 * ถ้าย่อจนถึงขนาดที่เล็กที่สุดที่ยังอ่านออกแล้วยังไม่พอ ค่อยยอมตัดเป็นสองบรรทัด
 */
export function fitLabel(
  ctx: TextMeasurer,
  label: string,
  maxWidth: number,
): { lines: string[]; fontSize: number } {
  for (let size = LABEL_FONT_MAX; size >= LABEL_FONT_MIN; size -= 2) {
    setLabelFont(ctx, size);
    if (ctx.measureText(label).width <= maxWidth) return { lines: [label], fontSize: size };
  }

  setLabelFont(ctx, LABEL_FONT_MIN);
  return { lines: splitToWidth(ctx, label, maxWidth), fontSize: LABEL_FONT_MIN };
}

/** ตัดตามช่องว่างก่อน ถ้ายังไม่พอค่อยตัดทีละอักขระ */
function splitToWidth(
  ctx: TextMeasurer,
  label: string,
  maxWidth: number,
): string[] {
  const lines: string[] = [];
  let line = '';

  const flush = () => {
    if (line) lines.push(line);
    line = '';
  };

  for (const token of label.split(' ')) {
    const candidate = line ? `${line} ${token}` : token;
    if (ctx.measureText(candidate).width <= maxWidth) {
      line = candidate;
      continue;
    }
    flush();

    // คำเดียวยังกว้างเกิน — จำเป็นต้องตัดกลางคำ
    let chunk = '';
    for (const ch of token) {
      if (ctx.measureText(chunk + ch).width > maxWidth && chunk) {
        lines.push(chunk);
        chunk = ch;
      } else {
        chunk += ch;
      }
    }
    line = chunk;
  }
  flush();
  return lines;
}
